import { handleBreakpoints, type ResponsiveStyleValue } from '@mui/system';
import { pxToRem } from '@packages/themes/utils';
import { type } from '../../../../theme';
import { makeStyles } from '../../styles/tss-react';
import { TextFieldSize } from '../TextField/types';

// TODO: Confirm whether Dropdown focus should use a dedicated v4 semantic token instead of brand charge blue.
const DROPDOWN_FOCUS_BORDER_COLOR = '#A5C9FF';

export interface DropdownStyleProps {
  fullWidth: boolean;
  inputSize: ResponsiveStyleValue<TextFieldSize>;
}

function getControlHeight(size: TextFieldSize, px: typeof pxToRem) {
  return size === TextFieldSize.Small ? { height: px(44) } : { height: px(56) };
}

function getDropdownPadding(size: TextFieldSize, px: typeof pxToRem) {
  // Right padding clears the 16px chevron (offset 16) plus an 8px gap.
  return size === TextFieldSize.Small
    ? {
        padding: `${px(10)} ${px(40)} ${px(10)} ${px(16)}`,
      }
    : {
        padding: `${px(16)} ${px(40)} ${px(16)} ${px(16)}`,
      };
}

export const useStyles = makeStyles<
  DropdownStyleProps,
  'helper' | 'helperError' | 'placeholder'
>({
  name: 'Dropdown',
  uniqId: 'Dropdown',
})(
  (
    { palette, transitions, breakpoints },
    { fullWidth, inputSize },
    classes
  ) => ({
    root: {
      ...(fullWidth ? { width: '100%' } : {}),
      '&:has(.Mui-disabled)': {
        opacity: 0.4,
      },
      [`&:has(.${classes.helper}) .${classes.helper}`]: {
        color: palette.semantic.text.secondary,
      },
      [`&:has(.Mui-error) .${classes.helper}`]: {
        color: palette.semantic.text.error,
      },
    },
    label: {
      ...type['14_regular'],
      display: 'block',
      marginBottom: pxToRem(6),
      color: palette.semantic.text.primary,
    },
    labelHidden: {
      visibility: 'hidden',
      height: 0,
      marginBottom: 0,
    },
    control: {
      position: 'relative',
      boxSizing: 'border-box',
      minWidth: 0,
      borderRadius: pxToRem(8),
      borderStyle: 'solid',
      borderWidth: 1,
      borderColor: palette.semantic.border.secondary,
      backgroundColor: palette.semantic.bg.primary,
      transition: transitions.create(['border-color', 'border-width'], {
        duration: transitions.duration.shorter,
      }),
      ...handleBreakpoints(
        { theme: { breakpoints } },
        inputSize,
        (resolvedSize) => getControlHeight(resolvedSize, pxToRem)
      ),
      '&:hover:not(:has(.Mui-disabled)):not(:has(.Mui-error))': {
        borderColor: palette.semantic.border.primary,
      },
      '&:has(.Mui-focused):not(:has(.Mui-error))': {
        borderColor: DROPDOWN_FOCUS_BORDER_COLOR,
        borderWidth: pxToRem(1.5),
      },
      '&:has(.Mui-error)': {
        borderColor: palette.semantic.text.error,
        borderWidth: pxToRem(1.5),
      },
    },
    dropdown: {
      width: '100%',
      height: '100%',
      minWidth: 0,
      cursor: 'pointer',
      ...type['16_regular'],
      color: palette.semantic.text.primary,
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      ...handleBreakpoints(
        { theme: { breakpoints } },
        inputSize,
        (resolvedSize) => ({
          '& .MuiSelect-select': {
            ...getDropdownPadding(resolvedSize, pxToRem),
            boxSizing: 'border-box',
            height: '100%',
            minHeight: '100%',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
          },
        })
      ),
      '& .MuiSelect-icon': {
        right: pxToRem(16),
        width: pxToRem(16),
        height: pxToRem(16),
        color: palette.semantic.text.primary,
        pointerEvents: 'none',
      },
    },
    valueText: {
      display: 'block',
      width: '100%',
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    placeholder: {
      color: palette.semantic.text.secondary,
    },
    menu: {
      '& .MuiMenuItem-root': {
        ...type['16_regular'],
        padding: `${pxToRem(12)} ${pxToRem(16)}`,
        color: palette.semantic.text.primary,
        whiteSpace: 'normal',
        '&:hover': {
          backgroundColor: palette.semantic.bg.categoryHover,
        },
      },
      '& .Mui-selected': {
        backgroundColor: palette.semantic.bg.categoryActive,
        '&:hover': {
          backgroundColor: palette.semantic.bg.categoryActive,
        },
      },
      '& .Mui-focusVisible': {
        backgroundColor: 'transparent',
      },
    },
    helper: {
      ...type['14_regular'],
      marginTop: pxToRem(6),
      minHeight: pxToRem(20),
      display: 'flex',
      alignItems: 'center',
      color: palette.semantic.text.secondary,
    },
    helperError: {
      gap: pxToRem(4),
    },
  })
);
